/**
 * Reports API
 *
 * Functions for interacting with the reports endpoints.
 */

import { api } from './client';

// =============================================================================
// Common Types
// =============================================================================

export interface DateRangeParams {
  startDate: string;
  endDate: string;
  format?: 'json' | 'csv';
}

// =============================================================================
// Payroll Report Types
// =============================================================================

export interface PayrollCrewData {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  totalHours: number;
  totalPay: number;
  assignmentCount: number;
  eventCount: number;
}

export interface PayrollReportResponse {
  report: PayrollCrewData[];
  totals: {
    totalHours: number;
    totalPay: number;
    totalAssignments: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// =============================================================================
// Event Cost Report Types
// =============================================================================

export interface EventCostData {
  eventId: string;
  eventName: string;
  eventDate: string;
  venue: string;
  sport: string | null;
  eventType: string | null;
  totalCost: number;
  totalHours: number;
  crewCount: number;
  roleCount: number;
}

export interface EventCostsReportResponse {
  report: EventCostData[];
  totals: {
    totalCost: number;
    totalHours: number;
    eventCount: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export interface CostByCategoryData {
  category: string;
  totalCost: number;
  totalHours: number;
  eventCount: number;
  assignmentCount: number;
}

export interface CostsByCategoryResponse {
  report: CostByCategoryData[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// =============================================================================
// Crew Report Types
// =============================================================================

export interface CrewUtilizationData {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  assignedCount: number;
  confirmedCount: number;
  declinedCount: number;
  totalHours: number;
  totalPay: number;
  confirmationRate: number;
}

export interface CrewUtilizationResponse {
  report: CrewUtilizationData[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export interface AvailabilityAnalyticsData {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  availableCount: number;
  notAvailableCount: number;
  maybeCount: number;
  totalResponses: number;
  responseRate: number;
  availabilityRate: number;
}

export interface AvailabilityAnalyticsResponse {
  report: AvailabilityAnalyticsData[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// =============================================================================
// Operations Report Types
// =============================================================================

export interface StaffingStatusData {
  eventId: string;
  eventName: string;
  eventDate: string;
  venue: string;
  rolesNeeded: number;
  rolesFilled: number;
  rolesConfirmed: number;
  fillRate: number;
  confirmRate: number;
}

export interface StaffingStatusResponse {
  report: StaffingStatusData[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export interface AssignmentResponseRatesData {
  period: string;
  totalAssignments: number;
  confirmedCount: number;
  declinedCount: number;
  pendingCount: number;
  confirmRate: number;
  declineRate: number;
}

export interface AssignmentResponseRatesResponse {
  report: AssignmentResponseRatesData[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// =============================================================================
// Crew Individual Report Types
// =============================================================================

export interface CrewPaymentHistoryData {
  assignmentId: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  roleName: string;
  effectiveRate: number | null;
  effectiveRateType: string | null;
  hoursWorked: number | null;
  /** True when hoursWorked is the event's crew-call-to-end span, not an entry. */
  hoursDefaulted: boolean;
  totalPay: number | null;
  status: string;
}

export interface CrewPaymentHistoryResponse {
  crew: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  report: CrewPaymentHistoryData[];
  totals: {
    totalHours: number;
    totalPay: number;
    assignmentCount: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export interface TimesheetEntryData {
  crewId: string;
  crewFirstName: string;
  crewLastName: string;
  crewEmail: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  venue: string;
  roleName: string;
  effectiveRate: number | null;
  effectiveRateType: string | null;
  hoursWorked: number | null;
  /** True when hoursWorked is the event's crew-call-to-end span, not an entry. */
  hoursDefaulted: boolean;
  checkInTime: string | null;
  checkOutTime: string | null;
  totalPay: number | null;
  status: string;
}

export interface CrewTimesheetResponse {
  crew: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  report: TimesheetEntryData[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get payroll report
 */
export async function getPayrollReport(params: DateRangeParams): Promise<PayrollReportResponse> {
  const response = await api.get<PayrollReportResponse>('/reports/payroll', { params });
  return response.data;
}

/**
 * Get event costs report
 */
export async function getEventCostsReport(params: DateRangeParams): Promise<EventCostsReportResponse> {
  const response = await api.get<EventCostsReportResponse>('/reports/event-costs', { params });
  return response.data;
}

/**
 * Get costs by sport
 */
export async function getCostsBySport(params: DateRangeParams): Promise<CostsByCategoryResponse> {
  const response = await api.get<CostsByCategoryResponse>('/reports/costs-by-sport', { params });
  return response.data;
}

/**
 * Get costs by event type
 */
export async function getCostsByEventType(params: DateRangeParams): Promise<CostsByCategoryResponse> {
  const response = await api.get<CostsByCategoryResponse>('/reports/costs-by-event-type', { params });
  return response.data;
}

/**
 * Get crew utilization report
 */
export async function getCrewUtilization(params: DateRangeParams): Promise<CrewUtilizationResponse> {
  const response = await api.get<CrewUtilizationResponse>('/reports/crew-utilization', { params });
  return response.data;
}

/**
 * Get availability analytics report
 */
export async function getAvailabilityAnalytics(params: DateRangeParams): Promise<AvailabilityAnalyticsResponse> {
  const response = await api.get<AvailabilityAnalyticsResponse>('/reports/availability-analytics', { params });
  return response.data;
}

/**
 * Get staffing status report
 */
export async function getStaffingStatus(params: DateRangeParams): Promise<StaffingStatusResponse> {
  const response = await api.get<StaffingStatusResponse>('/reports/staffing-status', { params });
  return response.data;
}

/**
 * Get assignment response rates report
 */
export async function getAssignmentResponseRates(params: DateRangeParams): Promise<AssignmentResponseRatesResponse> {
  const response = await api.get<AssignmentResponseRatesResponse>('/reports/assignment-response-rates', { params });
  return response.data;
}

/**
 * Get crew payment history
 */
export async function getCrewPaymentHistory(crewId: string, params: DateRangeParams): Promise<CrewPaymentHistoryResponse> {
  const response = await api.get<CrewPaymentHistoryResponse>(`/reports/crew/${crewId}/history`, { params });
  return response.data;
}

/**
 * Get crew timesheet
 */
export async function getCrewTimesheet(crewId: string, params: DateRangeParams): Promise<CrewTimesheetResponse> {
  const response = await api.get<CrewTimesheetResponse>(`/reports/crew/${crewId}/timesheet`, { params });
  return response.data;
}

// =============================================================================
// CSV Download Helper
// =============================================================================

/**
 * Download a report as CSV
 */
export async function downloadReportCSV(
  endpoint: string,
  params: DateRangeParams,
  filename: string
): Promise<void> {
  const response = await api.get(endpoint, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
